<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1050px"
    scrollable
    persistent
  >
    <v-card class="critical-modal-card">
      <!-- Modal Header -->
      <v-card-title class="grey lighten-4 py-3 px-4 d-flex align-center justify-space-between border-bottom">
        <div class="d-flex align-center">
          <v-icon color="red darken-3" class="mr-2">mdi-shield-alert</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
              Formatos Oficiales BASC - Personal Crítico Alto
            </div>
            <div class="caption grey--text text--darken-2">
              <strong>Colaborador:</strong> {{ displayWorkerName }} | <strong>DNI:</strong> {{ displayWorkerDni }} | <strong>Puesto:</strong> {{ displayPositionName }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <!-- Zoom Controls -->
          <div class="d-none d-sm-flex align-center mr-3 px-2 py-1 rounded border-light bg-white">
            <v-btn icon x-small @click="zoomOut" :disabled="zoomScale <= 0.6">
              <v-icon x-small>mdi-minus</v-icon>
            </v-btn>
            <span class="caption font-weight-bold px-1">{{ Math.round(zoomScale * 100) }}%</span>
            <v-btn icon x-small @click="zoomIn" :disabled="zoomScale >= 1.4">
              <v-icon x-small>mdi-plus</v-icon>
            </v-btn>
            <v-btn icon x-small @click="resetZoom" title="Restablecer zoom">
              <v-icon x-small>mdi-restore</v-icon>
            </v-btn>
          </div>

          <!-- Botón Imprimir Formato Actual -->
          <v-btn
            small
            outlined
            color="primary"
            class="mr-2 text-none font-weight-bold"
            @click="printCurrentTab"
          >
            <v-icon small left>mdi-printer</v-icon>
            Imprimir Formato Actual
          </v-btn>

          <!-- Botón Imprimir Pack Completo -->
          <v-btn
            small
            color="primary"
            elevation="0"
            class="mr-2 text-none font-weight-bold"
            @click="printAllFormats"
          >
            <v-icon small left>mdi-printer-check</v-icon>
            Imprimir los 3 Formatos
          </v-btn>

          <v-btn icon small @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Selector de Pestañas de Formatos -->
      <div class="bg-white border-bottom">
        <v-tabs
          v-model="activeTab"
          color="primary"
          active-class="font-weight-bold"
          dense
        >
          <v-tab class="text-none caption">
            <v-icon x-small left color="red darken-3">mdi-file-certificate</v-icon>
            1. Estudio de Seguridad (SIG-ES-RH-FR-022)
          </v-tab>
          <v-tab class="text-none caption">
            <v-icon x-small left color="indigo">mdi-home-map-marker</v-icon>
            2. Croquis de Domicilio (SIG-CS-RH-FR-048)
          </v-tab>
          <v-tab class="text-none caption">
            <v-icon x-small left color="orange darken-3">mdi-shield-account</v-icon>
            3. Designación de Puesto Crítico (SIG-CS-RH-FR-039)
          </v-tab>
        </v-tabs>
      </div>

      <!-- Contenedor del Visor A4 -->
      <v-card-text class="pa-4 bg-light-neutral modal-body-scroll" style="max-height: 75vh; overflow-y: auto;">
        <div class="d-flex justify-center">
          <div
            class="scalable-print-area"
            :style="{
              transform: `scale(${zoomScale})`,
              transformOrigin: 'top center',
              transition: 'transform 0.15s ease'
            }"
          >
            <!-- FORMATO 1: ESTUDIO DE SEGURIDAD (SIG-ES-RH-FR-022) -->
            <div id="format-view-0" v-show="activeTab === 0">
              <critical-employee-security-study
                :worker-data="compiledWorkerData"
                :document-date="compiledDocumentDate"
              />
            </div>

            <!-- FORMATO 2: CROQUIS DE DOMICILIO (SIG-CS-RH-FR-048) -->
            <div id="format-view-1" v-show="activeTab === 1">
              <critical-address-sketch
                :worker-data="compiledWorkerData"
                :document-date="compiledDocumentDate"
              />
            </div>

            <!-- FORMATO 3: DESIGNACION DE PUESTO CRITICO (SIG-CS-RH-FR-039) -->
            <div id="format-view-2" v-show="activeTab === 2">
              <critical-position-designation
                :worker-data="compiledWorkerData"
                :document-date="compiledDocumentDate"
              />
            </div>

            <!-- CONTENEDOR OCULTO PARA IMPRIMIR LOS 3 JUNTOS -->
            <div id="format-view-all" style="display: none;">
              <critical-employee-security-study
                :worker-data="compiledWorkerData"
                :document-date="compiledDocumentDate"
              />
              <critical-address-sketch
                :worker-data="compiledWorkerData"
                :document-date="compiledDocumentDate"
              />
              <critical-position-designation
                :worker-data="compiledWorkerData"
                :document-date="compiledDocumentDate"
              />
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Pie del Modal -->
      <v-card-actions class="px-4 py-2 bg-white d-flex align-center justify-space-between">
        <div class="caption grey--text text--darken-2">
          <v-icon x-small color="red darken-2" class="mr-1">mdi-shield-check</v-icon>
          Documentación obligatoria para personal clasificado como <strong>Crítico Alto (BASC)</strong>
        </div>
        <div>
          <v-btn text color="grey darken-2" small class="text-none font-weight-medium mr-2" @click="closeModal">
            Cerrar
          </v-btn>
          <v-btn color="primary" small elevation="0" class="text-none font-weight-bold" @click="printAllFormats">
            <v-icon small left>mdi-printer</v-icon>
            Imprimir los 3 Formatos
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CriticalEmployeeSecurityStudy from "./CriticalEmployeeSecurityStudy.vue";
import CriticalAddressSketch from "./CriticalAddressSketch.vue";
import CriticalPositionDesignation from "./CriticalPositionDesignation.vue";

export default {
  name: "CriticalFormatsPrintModal",
  components: {
    CriticalEmployeeSecurityStudy,
    CriticalAddressSketch,
    CriticalPositionDesignation,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    workerData: {
      type: Object,
      default: () => ({}),
    },
    contractData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: 0,
      zoomScale: 1,
    };
  },
  computed: {
    compiledWorkerData() {
      const w = this.workerData || {};
      const c = this.contractData || {};

      return {
        WkrID: c.WkrID || w.WkrID || 0,
        NtpID: c.NtpID || w.NtpID || 0,
        WkrCode: w.WkrCode || w.Photocheck || c.WkrCode || "",
        NtpFullName:
          w.NtpFullName ||
          w.FullName ||
          w.WorkerName ||
          c.NtpFullName ||
          c.WorkerName ||
          "",
        PrsDocumentNumber:
          w.PrsDocumentNumber ||
          w.DNI ||
          w.Dni ||
          c.PrsDocumentNumber ||
          c.DNI ||
          "",
        PrsBirthDate: w.PrsBirthDate || w.BirthDate || "",
        ExpeditionPlace: w.ExpeditionPlace || w.PrsExpeditionPlace || "",
        City: w.DistrictName || w.PrsDistrict || w.City || "",
        Province: w.ProvinceName || w.PrsProvince || w.Province || "",
        Country: w.CountryName || w.PrsCountry || "PERÚ",
        License: w.PrsDriverLicense || w.DriverLicense || "",
        TypeCivilStatus:
          c.TypeCivilStatus ||
          w.TypeCivilStatus ||
          w.PrsCivilStatus ||
          1,
        Address:
          w.PadAddress ||
          w.Address ||
          w.PrsAddress ||
          c.PadAddress ||
          "",
        Phone:
          w.NumberPhone ||
          w.Phone ||
          w.PrsPhone ||
          c.NumberPhone ||
          "",
        PstName: c.PstName || w.PstName || "",
        AreName: c.AreName || w.AreName || "",
        AgtBeginDate:
          c.AgtBeginDate ||
          w.AgtBeginDate ||
          new Date().toISOString().substring(0, 10),
        PensionRegimeName:
          c.TypePensionRegimenName ||
          (c.TypePensionRegimen === 2 ? "ONP" : "AFP"),
        CUSPP: c.CUSPP || "",
      };
    },
    compiledDocumentDate() {
      return (
        this.contractData?.AgtBeginDate ||
        this.workerData?.AgtBeginDate ||
        new Date().toISOString().substring(0, 10)
      );
    },
    displayWorkerName() {
      return this.compiledWorkerData.NtpFullName || "Colaborador";
    },
    displayWorkerDni() {
      return this.compiledWorkerData.PrsDocumentNumber || "-";
    },
    displayPositionName() {
      return this.compiledWorkerData.PstName || "-";
    },
  },
  methods: {
    closeModal() {
      this.$emit("input", false);
    },
    zoomIn() {
      if (this.zoomScale < 1.4) {
        this.zoomScale = +(this.zoomScale + 0.1).toFixed(1);
      }
    },
    zoomOut() {
      if (this.zoomScale > 0.6) {
        this.zoomScale = +(this.zoomScale - 0.1).toFixed(1);
      }
    },
    resetZoom() {
      this.zoomScale = 1;
    },

    printCurrentTab() {
      const containerId = `format-view-${this.activeTab}`;
      const el = document.getElementById(containerId);
      if (!el) return;

      const titles = [
        "SIG-ES-RH-FR-022_Estudio_Seguridad",
        "SIG-CS-RH-FR-048_Croquis_Domicilio",
        "SIG-CS-RH-FR-039_Designacion_Puesto_Critico",
      ];
      const title = `${titles[this.activeTab]}_${this.displayWorkerDni}`;
      this.executePrintHtml(el.innerHTML, title);
    },

    printAllFormats() {
      const el = document.getElementById("format-view-all");
      if (!el) return;

      const title = `Formatos_BASC_Critico_Alto_${this.displayWorkerDni}`;
      this.executePrintHtml(el.innerHTML, title);
    },

    executePrintHtml(htmlContent, title) {
      const win = window.open("", "_blank", "width=920,height=950");
      if (!win) {
        this.$fun.alert(
          "El navegador bloqueó la ventana emergente de impresión. Habilite las ventanas emergentes para este sitio.",
          "warning"
        );
        return;
      }

      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${title}</title>
            <style>
              @page {
                size: A4 portrait;
                margin: 8mm 12mm;
              }
              * {
                box-sizing: border-box;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: Arial, sans-serif;
                color: #000;
                background: #fff !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .critical-document-wrapper {
                width: 100%;
              }
              .page-sheet {
                background: #ffffff;
                width: 100%;
                max-width: 100%;
                min-height: 275mm;
                margin: 0 auto;
                padding: 6mm 10mm;
                page-break-after: always !important;
                break-after: page !important;
                position: relative;
                box-sizing: border-box;
              }
              .page-sheet:last-child {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              .header-table {
                width: 100%;
                border-collapse: collapse;
                border: 1px solid #000;
              }
              .header-table td {
                border: 1px solid #000;
                vertical-align: middle;
                padding: 5px 6px;
              }
              .header-logo-cell {
                width: 25%;
                text-align: center;
              }
              .company-logo {
                max-width: 120px;
                height: auto;
                display: block;
                margin: 0 auto;
              }
              .header-title-cell {
                width: 45%;
                text-align: center;
              }
              .main-title {
                font-size: 13px;
                font-weight: bold;
                line-height: 1.25;
                text-transform: uppercase;
              }
              .header-meta-cell {
                width: 30%;
                font-size: 8.5px;
                line-height: 1.35;
                padding-left: 6px !important;
              }
              .reference-table {
                width: 100%;
                border-collapse: collapse;
                border: 1px solid #000;
                border-top: none;
                font-size: 11px;
              }
              .reference-table td {
                padding: 5px 8px;
                border-right: 1px solid #000;
              }
              .reference-cell { width: 65%; }
              .date-cell { width: 35%; text-align: right; border-right: none !important; }
              .section-notice {
                font-size: 10px;
                line-height: 1.35;
                text-align: justify;
                margin-top: 6px;
              }
              .section-heading {
                font-size: 11.5px;
                font-weight: bold;
                text-decoration: underline;
                margin-top: 8px;
                margin-bottom: 5px;
              }
              .section-banner {
                font-size: 11.5px;
                font-weight: bold;
                padding: 4px 8px;
                border-top: 1px solid #000;
                border-bottom: 1px solid #000;
                background-color: #fafafa;
                margin-top: 8px;
              }
              .sub-heading {
                font-size: 11px;
                font-weight: bold;
                margin-top: 6px;
              }
              .font-size-11 { font-size: 10.5px; }
              .font-size-115 { font-size: 11px; }
              .font-size-12 { font-size: 11.5px; }
              .line-height-14 { line-height: 1.35; }
              .text-justify { text-align: justify; }
              .font-weight-bold { font-weight: bold; }
              .mt-1 { margin-top: 4px; }
              .mt-2 { margin-top: 7px; }
              .mt-3 { margin-top: 10px; }
              .mt-4 { margin-top: 14px; }
              .mt-6 { margin-top: 20px; }
              .mb-1 { margin-bottom: 4px; }
              .mb-2 { margin-bottom: 7px; }
              .mb-3 { margin-bottom: 10px; }
              .mr-1 { margin-right: 4px; }
              .mr-2 { margin-right: 8px; }
              .mr-3 { margin-right: 12px; }
              .mr-4 { margin-right: 16px; }
              .ml-3 { margin-left: 12px; }
              .ml-4 { margin-left: 16px; }
              .field-row {
                display: flex;
                align-items: baseline;
                font-size: 11px;
                margin-bottom: 4px;
              }
              .field-row-split {
                display: flex;
                gap: 12px;
                align-items: baseline;
                font-size: 11px;
                margin-bottom: 4px;
              }
              .field-item { display: flex; flex: 1; align-items: baseline; }
              .field-label { font-weight: bold; white-space: nowrap; margin-right: 6px; }
              .field-dots { flex: 1; border-bottom: 1px dotted #000; min-height: 15px; padding: 0 4px; }
              .field-suffix { margin-left: 6px; white-space: nowrap; font-size: 10px; }
              .civil-status-row { display: flex; align-items: center; font-size: 11px; margin: 5px 0; }
              .checkbox-box { display: inline-flex; align-items: center; margin-right: 12px; font-size: 11px; }
              .box-mark { display: inline-block; width: 13px; height: 13px; border: 1px solid #000; margin-right: 4px; text-align: center; line-height: 12px; font-size: 10px; font-weight: bold; }
              .checkbox-square { display: inline-block; width: 15px; height: 15px; border: 1px solid #000; }
              .grid-table { width: 100%; border-collapse: collapse; font-size: 10px; }
              .grid-table th, .grid-table td { border: 1px solid #000; padding: 3px 5px; }
              .grid-table th { background-color: #f7f7f7; font-weight: bold; text-align: center; }
              .grid-table td { height: 16px; }
              .emergency-line { font-size: 10.5px; }
              .write-line { border-bottom: 1px solid #000; height: 18px; }
              .values-row { display: flex; justify-content: space-between; font-size: 11px; }
              .fingerprints-container { display: flex; justify-content: space-around; gap: 8px; margin-top: 12px; }
              .fingerprint-column { display: flex; flex-direction: column; align-items: center; flex: 1; }
              .fingerprint-box { width: 80px; height: 110px; border: 1px solid #000; background: #fff; }
              .fingerprint-label { font-size: 9.5px; text-align: center; margin-top: 4px; line-height: 1.2; }
              .divider-line { border: none; border-top: 1px solid #000; margin: 10px 0; }
              .exclusive-use-title { text-align: center; font-weight: bold; font-size: 11.5px; }
              .company-eval-table { width: 100%; border-collapse: collapse; border: 1px solid #000; }
              .company-eval-table td { border: 1px solid #000; vertical-align: top; padding: 8px; }
              .eval-signature-cell { width: 50%; text-align: center; }
              .signature-line-company { width: 80%; margin: 35px auto 4px auto; border-bottom: 1px solid #000; }
              .eval-signer-title { font-size: 10px; font-weight: bold; line-height: 1.25; }
              .eval-status-cell { width: 50%; font-size: 10.5px; }
              .page-footer-note {
                font-size: 9px;
                font-style: italic;
                text-align: center;
                position: absolute;
                bottom: 12px;
                left: 0;
                right: 0;
              }
              .meta-row { display: flex; align-items: baseline; font-size: 11.5px; }
              .meta-label { font-weight: bold; margin-right: 6px; white-space: nowrap; }
              .meta-value { flex: 1; border-bottom: 1px dashed #000; padding: 0 4px; min-height: 16px; }
              .meta-value-short { width: 150px; border-bottom: 1px dashed #000; padding: 0 4px; min-height: 16px; }
              .sketch-explanation { font-size: 11.5px; line-height: 1.35; }
              .sketch-drawing-box { width: 100%; height: 500px; border: 1px solid #000; display: flex; align-items: center; justify-content: center; }
              .sketch-inner-hint { font-size: 11px; color: #ccc; }
              .sketch-signature-table { width: 100%; border-collapse: collapse; }
              .sketch-signature-table td { border: 1px solid #000; padding: 0; vertical-align: top; }
              .worker-boxes-wrapper { display: flex; height: 90px; border-bottom: 1px solid #000; }
              .signature-box-inner { flex: 3; border-right: 1px solid #000; }
              .fingerprint-box-inner { flex: 1; }
              .admin-box-inner { height: 90px; border-bottom: 1px solid #000; }
              .cell-label { font-size: 10.5px; font-weight: bold; text-align: center; padding: 4px; }
              .position-box-container { padding: 0 30px; }
              .position-row { display: flex; align-items: center; }
              .position-label { width: 170px; font-weight: bold; font-size: 11.5px; }
              .position-frame { flex: 1; border: 1px solid #000; height: 32px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 11.5px; }
              .signatures-table { width: 100%; border-collapse: collapse; }
              .signatures-table td { vertical-align: top; padding: 0 12px; }
              .sig-boxes-duo { display: flex; height: 90px; border: 1px solid #000; }
              .sig-space { flex: 3; border-right: 1px solid #000; }
              .fingerprint-space { flex: 1; }
              .sig-labels-duo { display: flex; border: 1px solid #000; border-top: none; font-size: 10.5px; font-weight: bold; text-align: center; }
              .sig-label-part { flex: 3; border-right: 1px solid #000; padding: 4px; }
              .fingerprint-label-part { flex: 1; padding: 4px; }
              .sig-space-single { height: 90px; border: 1px solid #000; }
              .sig-label-single { border: 1px solid #000; border-top: none; font-size: 10.5px; font-weight: bold; text-align: center; padding: 4px; }
              .d-flex { display: flex; }
              .justify-space-between { justify-content: space-between; }
              .align-center { align-items: center; }
              .flex-wrap { flex-wrap: wrap; }
              .text-center { text-align: center; }
              .pl-4 { padding-left: 16px; }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
        </html>
      `);

      win.document.close();
      win.focus();
      setTimeout(() => {
        win.print();
        win.close();
      }, 400);
    },
  },
};
</script>

<style scoped>
.critical-modal-card {
  border-radius: 8px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.border-light {
  border: 1px solid #e2e8f0;
}

.bg-light-neutral {
  background-color: #f1f5f9;
}

.modal-body-scroll {
  min-height: 480px;
}

.scalable-print-area {
  width: 100%;
  max-width: 780px;
}
</style>
